import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  addDays,
  isValidIsoDate,
  startOfWeek,
  type CopySkippedEntry,
  type CopyWeekMode,
  type CopyWeekResult,
} from '@tt/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Alert, Spinner } from '@/components/ui/misc';
import { Segmented } from '@/components/ui/segmented';
import { describeError } from '@/lib/errors';
import { formatDate, formatHours } from '@/lib/format';
import { useCopyWeek, useMe } from '@/lib/queries';
import { useFormat } from '@/lib/use-format';
import { useTimesheetLock } from '@/lib/use-timesheet-lock';

interface CopyWeekDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Any date inside the week being copied. */
  sourceDate: string;
  /** Whose timesheet (admins only); omit for your own. */
  userId?: string;
  /** Shown in the description so an admin sees whose week they are copying. */
  userName?: string;
  /** Called after a successful copy, e.g. to navigate to the target week. */
  onCopied?: (result: CopyWeekResult) => void;
}

/**
 * Copies a week onto another week of the same user. A dry run is requested whenever the
 * inputs change, so the confirm button can say exactly what will happen (FR2).
 */
export function CopyWeekDialog({
  open,
  onOpenChange,
  sourceDate,
  userId,
  userName,
  onCopied,
}: CopyWeekDialogProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { data: me } = useMe();
  const lock = useTimesheetLock();
  const weekStartDay = me?.organization.weekStartDay ?? 1;
  const sourceWeekStart = startOfWeek(sourceDate, weekStartDay);

  const [targetDate, setTargetDate] = useState(() => addDays(sourceWeekStart, 7));
  const [mode, setMode] = useState<CopyWeekMode>('ADD');
  const [includeDescriptions, setIncludeDescriptions] = useState(true);
  const [preview, setPreview] = useState<CopyWeekResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const copy = useCopyWeek();

  const targetWeekStart = useMemo(
    () => (isValidIsoDate(targetDate) ? startOfWeek(targetDate, weekStartDay) : null),
    [targetDate, weekStartDay],
  );
  const sameWeek = targetWeekStart === sourceWeekStart;
  // The server refuses a copy whose target week is closed at all, and a week whose first day is
  // open has no closed day in it, so the week start answers for the whole week. Copying *out* of
  // a closed week is fine and deliberately not checked: that only reads it.
  const targetClosed = targetWeekStart !== null && lock.isLocked(targetWeekStart);

  useEffect(() => {
    if (open) {
      setTargetDate(addDays(sourceWeekStart, 7));
      setMode('ADD');
      setIncludeDescriptions(true);
      setPreview(null);
      setError(null);
    }
  }, [open, sourceWeekStart]);

  // Dry run whenever the inputs change, so the dialog can describe the outcome up front.
  useEffect(() => {
    if (!open || !targetWeekStart || sameWeek || targetClosed) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setError(null);
    copy.mutate(
      { userId, sourceDate, targetDate, mode, includeDescriptions, preview: true },
      {
        onSuccess: (result) => {
          if (!cancelled) setPreview(result);
        },
        onError: (err) => {
          if (!cancelled) {
            setPreview(null);
            setError(describeError(err, t));
          }
        },
      },
    );
    return () => {
      cancelled = true;
    };
    // Intentionally keyed on the inputs only: the mutation and translator are stable.
  }, [
    open,
    userId,
    sourceDate,
    targetDate,
    targetWeekStart,
    sameWeek,
    targetClosed,
    mode,
    includeDescriptions,
  ]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    copy.mutate(
      { userId, sourceDate, targetDate, mode, includeDescriptions, preview: false },
      {
        onSuccess: (result) => {
          toast.success(
            t('time.copy.done', {
              count: result.copiedCount,
              hours: formatHours(result.copiedMinutes, fmt),
              week: formatDate(result.targetWeekStart, fmt),
            }),
          );
          if (result.skipped.length) {
            toast.warning(t('time.copy.skippedToast', { count: result.skipped.length }));
          }
          onOpenChange(false);
          onCopied?.(result);
        },
        onError: (err) => setError(describeError(err, t)),
      },
    );
  };

  const nothingToCopy = preview !== null && preview.sourceEntryCount === 0;
  const targetHasEntries = (preview?.targetMinutesBefore ?? 0) > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <DialogHeader>
            <DialogTitle>{t('time.copy.title')}</DialogTitle>
            <DialogDescription>
              {userName
                ? t('time.copy.descriptionForUser', {
                    name: userName,
                    week: formatDate(sourceWeekStart, fmt),
                  })
                : t('time.copy.description', { week: formatDate(sourceWeekStart, fmt) })}
            </DialogDescription>
          </DialogHeader>

          {error ? <Alert variant="error">{error}</Alert> : null}

          <Field
            label={t('time.copy.targetWeek')}
            hint={
              targetWeekStart
                ? t('time.copy.targetWeekHint', { week: formatDate(targetWeekStart, fmt) })
                : undefined
            }
            error={
              sameWeek
                ? t('time.copy.sameWeek')
                : targetClosed
                  ? t('time.copy.targetClosed')
                  : undefined
            }
            required
          >
            {(p) => (
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  {...p}
                  type="date"
                  value={targetDate}
                  onChange={(e) => setTargetDate(e.target.value)}
                  className="w-44"
                  required
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setTargetDate(addDays(sourceWeekStart, 7))}
                >
                  {t('time.copy.nextWeek')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setTargetDate(addDays(sourceWeekStart, -7))}
                >
                  {t('time.copy.previousWeek')}
                </Button>
              </div>
            )}
          </Field>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">{t('time.copy.mode')}</legend>
            <Segmented<CopyWeekMode>
              value={mode}
              onChange={setMode}
              aria-label={t('time.copy.mode')}
              options={[
                { value: 'ADD', label: t('time.copy.modeAdd') },
                { value: 'REPLACE', label: t('time.copy.modeReplace') },
              ]}
            />
            <p className="text-xs text-muted-foreground">
              {mode === 'ADD' ? t('time.copy.modeAddHelp') : t('time.copy.modeReplaceHelp')}
            </p>
          </fieldset>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={includeDescriptions}
              onChange={(e) => setIncludeDescriptions(e.target.checked)}
            />
            {t('time.copy.includeDescriptions')}
          </label>

          <div aria-live="polite">
            {copy.isPending && !preview ? <Spinner label={t('time.copy.checking')} /> : null}
            {preview ? <CopyPreview result={preview} /> : null}
            {targetHasEntries && mode === 'REPLACE' ? (
              <Alert variant="warning" className="mt-2">
                {t('time.copy.replaceWarning', {
                  count: preview?.deletedCount ?? 0,
                  hours: formatHours(preview?.deletedMinutes ?? 0, fmt),
                })}
              </Alert>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('app.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={copy.isPending || sameWeek || targetClosed || nothingToCopy}
            >
              {t('time.copy.confirm')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CopyPreview({ result }: { result: CopyWeekResult }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  if (result.sourceEntryCount === 0) {
    return <Alert variant="warning">{t('time.copy.emptySource')}</Alert>;
  }
  return (
    <div className="rounded-md border bg-muted/30 p-3 text-sm">
      <p>
        {t('time.copy.summary', {
          count: result.copiedCount,
          hours: formatHours(result.copiedMinutes, fmt),
        })}
      </p>
      <p className="text-muted-foreground">
        {t('time.copy.targetTotals', {
          before: formatHours(result.targetMinutesBefore, fmt),
          after: formatHours(result.targetMinutesAfter, fmt),
        })}
      </p>
      {result.skipped.length > 0 ? (
        <details className="mt-2">
          <summary className="cursor-pointer text-amber-700">
            {t('time.copy.skipped', { count: result.skipped.length })}
          </summary>
          <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
            {result.skipped.map((s: CopySkippedEntry, i) => (
              <li key={`${s.entryDate}-${s.projectName}-${s.activityName}-${i}`}>
                {formatDate(s.entryDate, fmt)} · {s.projectName} / {s.activityName} ·{' '}
                {t(`time.copy.skipReason.${s.reason}`)}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
